
import axios from "../../bootstrap/axios";

function extractRoles(user) {
  if (!user) return [];

  // New Laravel structure:
  // { id: 1, name: "...", role: "admin" }
  if (typeof user.role === "string" && user.role) {
    return [user.role];
  }

  // Compatibility with your previous structure:
  // { roles: [{ name: "admin" }] }
  if (Array.isArray(user.roles)) {
    return user.roles
      .map(role => typeof role === "string" ? role : role.name)
      .filter(Boolean);
  }

  return [];
}

export default {
  namespaced: true,

  state: () => ({
    user: null,
    roles: [],
    status: "idle", // idle | loading | ready
    bootstrapped: false,
  }),

  getters: {
    isAuthenticated: state => Boolean(state.user),

    isAdmin: state =>
      state.roles.includes("admin"),

    isCustomer: state =>
      state.roles.includes("customer"),

    // Keep compatibility with existing components.
    isClient: state =>
      state.roles.includes("customer") ||
      state.roles.includes("client"),

    isGarage: state =>
      state.roles.includes("garage"),

    role: state =>
      state.roles[0] || null,

    user: state =>
      state.user,

    isLoading: state =>
      state.status === "loading",
  },

  mutations: {
    SET_STATUS(state, status) {
      state.status = status;
    },

    SET_USER(state, user) {
      state.user = user || null;
      state.roles = extractRoles(user);
    },

    CLEAR_AUTH(state) {
      state.user = null;
      state.roles = [];
    },

    SET_BOOTSTRAPPED(state, value) {
      state.bootstrapped = value;
    },
  },

  actions: {
    // Initialize session on first load.
    // Use force:true after login/register to refresh an existing session.
    async bootstrap({ state, commit }, { force = false } = {}) {
      if (state.bootstrapped && !force) {
        return state.user;
      }

      commit("SET_STATUS", "loading");

      try {
        const { data } = await axios.get("/api/me");

        const user = data.user || null;

        if (!user || user.is_active === false) {
          commit("CLEAR_AUTH");
          return null;
        }

        commit("SET_USER", user);

        return user;
      } catch (error) {
        commit("CLEAR_AUTH");
        return null;
      } finally {
        commit("SET_BOOTSTRAPPED", true);
        commit("SET_STATUS", "ready");
      }
    },




    async login({ commit, dispatch }, payload) {
      commit("SET_STATUS", "loading");

      try {
        const csrf = await axios.get("/sanctum/csrf-cookie");

        console.log("1. CSRF STATUS:", csrf.status);

        const response = await axios.post("/api/login", {
          email: payload.email,
          password: payload.password,
        });

        console.log("2. LOGIN STATUS:", response.status);
        console.log("3. LOGIN RESPONSE:", response.data);
        console.log("4. LOGIN USER:", response.data?.user);

        const user = await dispatch("bootstrap", {
          force: true,
        });

        console.log("5. BOOTSTRAP USER:", user);

        if (!user) {
          throw new Error(
            "Connexion réussie, mais impossible de récupérer le profil."
          );
        }

        return {
          ...response.data,
          user,
        };

      } catch (error) {
        console.error("LOGIN ERROR:", {
          status: error.response?.status,
          response: error.response?.data,
          message: error.message,
        });

        throw error;

      } finally {
        commit("SET_STATUS", "ready");
      }
    },



    async register({ commit, dispatch }, payload) {
      commit("SET_STATUS", "loading");

      try {
        await axios.get("/sanctum/csrf-cookie");

        const { data } = await axios.post("/api/register", payload);

        // This assumes registration also starts a session.
        // If it doesn't, implement login after registration.
        const user = await dispatch("bootstrap", { force: true });

        if (!user) {
          throw new Error(
            "Compte créé, mais aucune session n'a été ouverte."
          );
        }

        return {
          ...data,
          user,
        };
      } catch (error) {
        // Don't clear the previous session on a registration
        // validation failure.
        throw error;
      } finally {
        commit("SET_STATUS", "ready");
      }
    },

    async logout({ commit }) {
      commit("SET_STATUS", "loading");

      try {
        await axios.post("/api/logout");

        commit("CLEAR_AUTH");
        commit("SET_BOOTSTRAPPED", true);
      } finally {
        commit("SET_STATUS", "ready");
      }
    },

    // Revalidate the current session when needed.
    async refreshUser({ dispatch }) {
      return dispatch("bootstrap", { force: true });
    },
  },
};
